import React from 'react';
import classNames from 'classnames';
import useTasks from '@modules/TasksModule/hooks/useTasks';
import TasksModule from '@modules/TasksModule';
import SoftPhoneModule from '@modules/SoftPhoneModule';
import InboxModule from '@modules/InboxModule';
import ToastModule from '@modules/ToastModule';
import { Headline, BodyText } from '@servicetitan/design-system';
import { Flex } from '@servicetitan/anvil2';
import { SpeedToLeadCallButton } from '../SpeedToLeadCallButton';
import { LogOutcomeSection } from '../LogOutcomeSection/LogOutcomeSection';
import { completeTask } from '@modules/SpeedToLeadTasksModule/mutations/completeTask';
import { SpeedToLeadTaskAttributes } from '@modules/SpeedToLeadTasksModule/types';
import { TaskOutcome } from '@api/generated/graphql';
import AuthModule from '@modules/AuthModule';
import { bookJobLinkToMonolith } from '@lib/utils/tenantLinks';
import styles from './SpeedToLeadTaskView.module.css';
import useTenantName from '@lib/hooks/useTenantName';

export interface SpeedToLeadTaskViewProps {
  taskId: string;
  show?: boolean;
}

export const SpeedToLeadTaskView: React.FC<SpeedToLeadTaskViewProps> = ({
  taskId,
  show = false,
}) => {
  const { taskById } = useTasks();
  const task = taskById[taskId];
  const tenantName = useTenantName(task?.tenantId);

  if (!task) {
    return null;
  }

  const attributes = task.attributes as SpeedToLeadTaskAttributes;
  const { contact, taskDetails, action } = attributes;

  // Call state is managed via task attributes by STL call event handlers
  const hasMadeCall = !!attributes.hasMadeCall;
  const hasCallConnected = !!attributes.hasCallConnected;
  const hasCallEnded = !!attributes.hasCallEnded;

  const customerPhone = contact.phoneNumbers[0] ?? '';

  const handleCall = async () => {
    // Set hasMadeCall before placing the call so that InboxModule's handleCallPlaced
    // (which fires first due to module registration order) sees it and skips task creation.
    TasksModule.setTaskAttribute(taskId, 'hasMadeCall', true);
    try {
      await SoftPhoneModule.placeOutboundCall(customerPhone, {
        name: tenantName || '',
        number: attributes.outboundNumber,
        tenantId: attributes.tenantId,
      });
    } catch {
      ToastModule.showToast({
        title: 'Failed to place call. Please try again.',
        status: 'critical',
      });
    }
  };

  const handleBookJob = () => {
    window.open(bookJobLinkToMonolith(attributes.tenantId), '_blank');
    AuthModule.resetAuthAge();
  };

  const handleSubmitOutcome = async (outcome: TaskOutcome, notes?: string) => {
    try {
      await completeTask({
        taskId,
        tenantId: attributes.tenantId,
        input: {
          outcome,
          notes,
        },
      });
      TasksModule.deleteTask(taskId);
      InboxModule.setSelectedTaskId(undefined as unknown as string);
      ToastModule.showToast({
        title: 'Outbound task complete',
        status: 'success',
      });
    } catch {
      ToastModule.showToast({
        title: 'Failed to log outcome. Please try again.',
        status: 'critical',
      });
    }
  };

  // Helper function to convert text with newlines into list items
  const renderBulletList = (text: string) => {
    const items = text
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    if (items.length === 0) return null;

    return (
      <ul className={styles.bulletList}>
        {items.map((item, index) => (
          <li key={index}>
            <BodyText size='small'>{item}</BodyText>
          </li>
        ))}
      </ul>
    );
  };

  // Format address
  const address = contact.address;
  const addressString = [
    address.street,
    address.unit,
    address.city,
    address.state,
    address.zip,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <div
      className={classNames(
        'w-100',
        'h-100',
        'of-y-auto',
        'p-x-3',
        'p-y-2',
        'bg-white',
        {
          'd-n': !show,
        },
      )}
    >
      {/* Customer Header */}
      <Flex direction='column' gap='2' className={styles.customerHeader}>
        <Flex justifyContent='space-between' alignItems='flex-start'>
          <div>
            <Headline size='large'>{contact.name}</Headline>
            <BodyText size='small' subdued>
              {addressString}
            </BodyText>
            <div className={styles.badge}>
              <BodyText size='xsmall'>Action: {action}</BodyText>
            </div>
          </div>
          <SpeedToLeadCallButton
            customerName={contact.name.split(' ')[0]}
            onCall={handleCall}
            onBookJob={handleBookJob}
            disabled={hasMadeCall}
            showBookJob={hasCallConnected}
            isCallConnected={hasCallConnected}
            isCallEnded={hasCallEnded}
          />
        </Flex>
      </Flex>

      {/* Log Outcome Section - shown after call is made */}
      {hasMadeCall && (
        <LogOutcomeSection
          liveAnswerOutcomeOptions={attributes.liveAnswerOutcomeOptions ?? []}
          noAnswerOutcomeOptions={attributes.noAnswerOutcomeOptions ?? []}
          onSubmit={handleSubmitOutcome}
          isCallEnded={hasMadeCall}
        />
      )}

      {/* Task Details Section */}
      <div className={styles.taskDetails}>
        <Headline size='small' className={styles.sectionHeading}>
          Task Details
        </Headline>

        <div className={styles.detailSection}>
          <BodyText size='small' bold>
            Script Opener:
          </BodyText>
          <div className={styles.quotedText}>
            <BodyText size='small'>"{taskDetails.scriptOpener}"</BodyText>
          </div>
        </div>

        <div className={styles.detailSection}>
          <BodyText size='small' bold>
            Lead Summary:
          </BodyText>
          {renderBulletList(taskDetails.summary)}
        </div>

        <div className={styles.detailSection}>
          <BodyText size='small' bold>
            What Has Been Attempted:
          </BodyText>
          {renderBulletList(taskDetails.history)}
        </div>

        <div className={styles.detailSection}>
          <BodyText size='small' bold>
            Goal of the Call:
          </BodyText>
          {renderBulletList(taskDetails.goal)}
        </div>
      </div>
    </div>
  );
};
